import { useCallback, useEffect, useState } from 'react';

import type { Channel } from '../../../api';
import { useApiClient } from '../../../lib/api/api-context';

/**
 * The photo of a channel as an object URL (S5 R14): fetched with the bearer token
 * (`getBlob`), only once the row is near the viewport (Proposal, to be validated: 300 channels
 * must not send 300 requests at once; where `IntersectionObserver` does not exist, at once). The
 * URL is revoked and the request aborted on unmount or when the channel changes. A failure leaves
 * `undefined`: the initials stay and nothing is shown (the photo is decorative).
 */
export function useChannelPhoto(
  channelId: Channel['id'],
  enabled: boolean,
): { readonly url: string | undefined; readonly watch: (element: Element | null) => void } {
  const api = useApiClient();
  const [element, setElement] = useState<Element | null>(null);
  // Without IntersectionObserver the photo loads at once (R14).
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || visible || element === null) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [enabled, visible, element]);

  useEffect(() => {
    if (!enabled || !visible) return undefined;
    const controller = new AbortController();
    let created: string | undefined;
    api.getBlob(`/channels/${encodeURIComponent(channelId)}/photo`, controller.signal).then(
      (blob) => {
        if (controller.signal.aborted) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      },
      () => {
        // R14: no photo, a 404 or an outage keep the initials; nothing to tell the trader.
      },
    );
    return () => {
      controller.abort();
      if (created !== undefined) URL.revokeObjectURL(created);
      setUrl(undefined);
    };
  }, [api, channelId, enabled, visible]);

  const watch = useCallback((node: Element | null) => {
    setElement(node);
  }, []);
  return { url, watch };
}
